import { useRef, useState } from "react";
import { API } from "../../lib/api";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { UploadCloud, Search, Plus, Trash2, RefreshCw, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { t } from "../../i18n";

function StatusBadge({ status }) {
  if (status === "pronto")
    return <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 rounded-sm gap-1"><CheckCircle2 className="h-3 w-3" />{t("Pronto")}</Badge>;
  if (status === "erro")
    return <Badge className="bg-destructive/15 text-destructive border-destructive/30 rounded-sm gap-1"><AlertTriangle className="h-3 w-3" />{t("Erro")}</Badge>;
  return <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 rounded-sm gap-1"><Loader2 className="h-3 w-3 animate-spin" />{t("Processando")}</Badge>;
}

export function MediaLibrary({ media, onUpload, onAdd, onDelete, onReprocess, uploading }) {
  const inputRef = useRef(null);
  const [q, setQ] = useState("");
  const [drag, setDrag] = useState(false);

  const filtered = media.filter((m) => m.nome_original.toLowerCase().includes(q.toLowerCase()));

  const handleFiles = (files) => {
    if (files && files.length) onUpload(Array.from(files));
  };

  return (
    <div className="flex flex-col h-full">
      <div
        className={`m-3 border-2 border-dashed rounded-sm p-4 text-center cursor-pointer transition-colors duration-200 ${
          drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
        }`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
        data-testid="media-dropzone"
      >
        <UploadCloud className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
        <p className="text-xs text-muted-foreground">{t("Arraste mídias ou clique. .mov .mp4 .webm .gif .png .jpg")}</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".mov,.mp4,.webm,.gif,.png,.apng,.jpg,.jpeg,.webp"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="media-file-input"
        />
      </div>

      {uploading.length > 0 && (
        <div className="px-3 pb-2 space-y-1">
          {uploading.map((u) => (
            <div key={u.name} className="text-xs">
              <div className="flex justify-between text-muted-foreground mb-0.5">
                <span className="truncate max-w-[140px]">{u.name}</span><span>{u.pct}%</span>
              </div>
              <div className="h-1 bg-secondary rounded-sm overflow-hidden">
                <div className="h-full bg-primary transition-all duration-200" style={{ width: `${u.pct}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="px-3 pb-2 relative">
        <Search className="h-3.5 w-3.5 absolute left-5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Buscar mídia")}
          className="pl-8 h-8 text-xs rounded-sm" data-testid="media-search" />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3" data-testid="media-scroll">
        <div className="grid grid-cols-2 gap-2">
        {filtered.map((m) => (
          <div key={m.id} className="border border-border/60 rounded-sm overflow-hidden bg-secondary/40 group"
            data-testid={`media-card-${m.id}`}>
            <div className="checker-bg aspect-video flex items-center justify-center relative">
              {m.status === "pronto" && m.has_webm === false && m.kind === "image" ? (
                <img src={`${API}/media/${m.id}/file?variant=thumb`} alt="" className="w-full h-full object-contain" />
              ) : m.status === "pronto" ? (
                <img src={`${API}/media/${m.id}/file?variant=thumb`} alt="" className="w-full h-full object-contain" />
              ) : (
                <Loader2 className="h-5 w-5 text-white/40 animate-spin" />
              )}
            </div>
            <div className="p-2">
              <div className="flex items-center justify-between gap-1 mb-1">
                <StatusBadge status={m.status} />
                {m.tem_alpha && <span className="text-[9px] font-mono text-primary">ALPHA</span>}
              </div>
              <p className="text-[11px] truncate text-muted-foreground" title={m.nome_original}>{m.nome_original}</p>
              {m.status === "erro" && <p className="text-[10px] text-destructive mt-1 line-clamp-2">{m.erro}</p>}
              <div className="flex gap-1 mt-2">
                {m.status === "pronto" && (
                  <button onClick={() => onAdd(m)}
                    className="flex-1 h-7 rounded-sm bg-primary/15 text-primary hover:bg-primary/25 transition-colors flex items-center justify-center gap-1 text-[11px]"
                    data-testid={`media-add-${m.id}`}>
                    <Plus className="h-3 w-3" />{t("Add")}
                  </button>
                )}
                {m.status === "erro" && (
                  <button onClick={() => onReprocess(m.id)}
                    className="flex-1 h-7 rounded-sm bg-secondary hover:bg-accent transition-colors flex items-center justify-center gap-1 text-[11px]"
                    data-testid={`media-retry-${m.id}`}>
                    <RefreshCw className="h-3 w-3" />{t("Reenviar")}
                  </button>
                )}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button className="h-7 w-7 rounded-sm bg-secondary hover:bg-destructive/20 hover:text-destructive transition-colors flex items-center justify-center"
                      data-testid={`media-delete-${m.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("Excluir mídia?")}</AlertDialogTitle>
                      <AlertDialogDescription>{m.nome_original}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("Cancelar")}</AlertDialogCancel>
                      <AlertDialogAction onClick={() => onDelete(m.id)}>{t("Excluir")}</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="col-span-2 text-xs text-muted-foreground text-center py-6">{t("Nenhuma mídia.")}</p>}
        </div>
      </div>
    </div>
  );
}
